import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import { ArrowLeft, Zap, MapPin, Tool } from "lucide-react";
import { SOCIAL_ICONS } from "data/staticData";
import { loginUser } from "../services/authService";
import { useAuth } from "context/AuthContext";

export const roleOptions = [
  { value: "1", label: "Director" },
  { value: "2", label: "Camera Operator" },
  { value: "3", label: "Audio Engineer" },
  { value: "4", label: "Lighting Technician" },
  { value: "5", label: "Video Editor" },
  { value: "6", label: "Stream Engineer" },
  { value: "7", label: "Production Manager" },
  { value: "8", label: "Graphics Designer" },
  { value: "9", label: "Videographer" },
  { value: "10", label: "Photographers" },
];

export const skillOptions = [
  { value: "13", label: "Director" },
  { value: "12", label: "Livestream Audio" },
  { value: "2", label: "Video Event" },
  { value: "3", label: "Video Music" },
  { value: "4", label: "Video Lifestyle" },
  { value: "5", label: "Photo Portrait" },
  { value: "6", label: "Photo Product" },
  { value: "7", label: "Photo Event" },
  { value: "8", label: "Photo Lifestyle" },
  { value: "9", label: "Audio Engineer" },
  { value: "10", label: "Creative Director" },
  { value: "11", label: "Livestream Director" },
  { value: "1", label: "Video Commercial" },
  { value: "14", label: "Video Weddings" },
  { value: "15", label: "Photo Weddings" },
  { value: "16", label: "Portrait Photo" },
  { value: "17", label: "Cinematographer" },
];

const getShortLocation = (location) => {
  if (!location?.address) return "California, United States";
  const parts = location.address.split(",").map((p) => p.trim());
  if (parts.length >= 2) {
    const country = parts[parts.length - 1];
    const city = parts[parts.length - 3] || parts[parts.length - 2];
    return `${city}, ${country}`;
  }
  return location.address;
};

export default function SignupSuccess({ data }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  // 1. Resolve Primary Role Label
  const primaryRoleLabel = roleOptions.find(
    (role) => role.value === data?.roles?.[0]
  )?.label;

  // 2. Resolve Skills Labels
  const selectedSkills = (data?.skills || [])
    .map((skillId) => skillOptions.find((opt) => opt.value === skillId)?.label)
    .filter(Boolean);

  const profileImage = data?.profilePreview || "/images/misc/tempProfile.jpg";

  const allFeaturedImages = (data?.featuredWork || []).flatMap(
    (work) => work.previews || []
  );

  const handleGoToDashboard = async () => {
    if (!data?.email || !data?.password) return;
    try {
      setLoading(true);
      const res = await loginUser({
        email: data.email,
        password: data.password,
      });
      login(res.token, res.user);

      if (res.user?.userTypeId === 1) {
        navigate("/dashboard");
      } else if (res.user?.userTypeId === 2) {
        navigate("/creator-dashboard");
      } else {
        navigate("/");
      }
    } catch (err) {
      console.error("Login failed", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    /* Changed justify-end to justify-start and added pt-20 for top space */
    <div className="min-h-screen w-full flex flex-col items-center justify-start pt-20 pb-20 bg-[#faf7f2] overflow-x-hidden px-4">

      {/* Changed rounded-t to rounded-[40px] so it looks like a floating card */}
      <div className="relative w-full max-w-[640px] rounded-[40px] bg-gradient-to-b from-[#0b0c16] to-[#02030a] px-6 pt-12 pb-12 shadow-2xl animate-slideUp">

        <div className="flex justify-center mb-10">
          <span className="px-8 py-2 text-sm tracking-[0.4em] rounded-full bg-[#2a2a2a] text-[#d6c6a5] border border-[#3a3a3a] font-medium">
            BEIGE
          </span>
        </div>

        {/* PROFILE PREVIEW CARD */}
        <div className="mx-auto w-full max-w-[420px] bg-[#fbfaf7] rounded-3xl p-5 shadow-xl mb-10 flex flex-col border border-[#E5E7EB]">
          <div className="flex items-center gap-3 mb-4">
            <img
              src={profileImage}
              alt="Profile"
              className="w-14 h-14 rounded-full object-cover bg-gray-200 border-2 border-white shadow-sm"
            />
            <div className="flex-1">
              <p className="font-bold text-base leading-tight text-black">
                {data?.firstName} {data?.lastName}
              </p>
              <p className="text-[11px] font-semibold text-[#9C8662] uppercase tracking-wider">
                {primaryRoleLabel || "Creative Professional"}
              </p>
              <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-1">
                <MapPin size={10} />
                <span>{getShortLocation(data?.location)}</span>
              </div>
            </div>
          </div>

          {/* STATS ROW */}
          <div className="flex gap-8 text-[10px] text-gray-500 mb-5 border-y border-gray-100 py-4">
            {data?.hourlyRate && (
              <div>
                <p className="font-bold text-black text-sm">${data.hourlyRate}</p>
                <p className="uppercase tracking-tighter text-[9px]">Rate / hr</p>
              </div>
            )}
            {data?.yoe && (
              <div>
                <p className="font-bold text-black text-sm">{data.yoe}y</p>
                <p className="uppercase tracking-tighter text-[9px]">Exp.</p>
              </div>
            )}
            {data?.workingDistance && (
              <div>
                <p className="font-bold text-black text-sm">
                  {data.workingDistance.replace("Upto ", "")}
                </p>
                <p className="uppercase tracking-tighter text-[9px]">Radius</p>
              </div>
            )}
          </div>

          {/* BIO SECTION */}
          <div className="mb-5">
             <p className="text-[13px] text-gray-600 leading-relaxed italic px-1">
               "{data?.bio || "Creative professional ready for new opportunities."}"
             </p>
          </div>

          {/* SKILLS SECTION */}
          {selectedSkills.length > 0 && (
            <div className="mb-5">
              <p className="text-[9px] uppercase font-bold text-gray-400 mb-2 tracking-[0.2em]">Skills</p>
              <div className="flex flex-wrap gap-1.5">
                {selectedSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-2.5 py-1 bg-gray-100 text-gray-700 text-[10px] font-medium rounded border border-gray-200"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* EQUIPMENT SECTION */}
          {data?.equipmentNames?.length > 0 && (
            <div className="mb-6">
              <p className="text-[9px] uppercase font-bold text-gray-400 mb-2 tracking-[0.2em]">Equipment</p>
              <div className="flex flex-wrap gap-1.5">
                {data.equipmentNames.map((item, index) => (
                  <span
                    key={index}
                    className="px-2.5 py-1 bg-[#f0f4ff] text-[#3b82f6] text-[10px] font-medium rounded border border-[#dbeafe]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* FEATURED WORK PREVIEW */}
          {allFeaturedImages.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mt-auto">
              {allFeaturedImages.slice(0, 2).map((imgUrl, index) => (
                <img
                  key={index}
                  src={imgUrl}
                  alt="Work Preview"
                  className="h-[100px] w-full object-cover rounded-xl border border-gray-100 shadow-sm"
                />
              ))}
            </div>
          )}
        </div>

        <div className="text-center px-4">
          <h2 className="text-[32px] font-semibold text-[#ECD8B9] mb-3 leading-tight">
            Profile Completed.
          </h2>
          <p className="text-sm text-[#737373] mb-8 max-w-xs mx-auto leading-relaxed">
            Your profile is now live. Production teams can now find and hire
            you.
          </p>
          <Button
            disabled={loading}
            onClick={handleGoToDashboard}
            className="w-full sm:w-auto px-12 py-7 rounded-full bg-[#e7d7bd] text-black font-bold hover:bg-[#dccaa9] disabled:opacity-60 transition-all shadow-lg text-base active:scale-95"
          >
            {loading ? "Logging in..." : "Go To Dashboard"}
          </Button>
        </div>
      </div>
    </div>
  );
}
